import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import {
  ButtonContainer,
  CarouselSlide,
  CarouselTrack,
  CarouselWrapper,
  CommentInput,
  CommentsButton,
  CommentsContainer,
  CommentsWrapper,
  Container,
  ContentContainer,
  HeaderActions,
  CancelDeleteButton,
  ConfirmDeleteButton,
  DeleteConfirmBar,
  Dot,
  DotAccomplished,
  DotSlides,
  ReviewTag,
  Footer,
  HeaderContent,
  Indicators,
  InputWrapper,
  LikeButton,
  PostUserContent,
  Tag,
  TagsContainer,
  UserAvatar,
  UserAvatarComments,
} from './styles';
import {
  IPostMedia,
  IShowComment,
  IUserPost,
} from '../../dtos/IPostProjectProps';
import {
  ArrowBackIcon,
  CommentsIcon,
  PencilIcon,
  SendMessageIcon,
  TrashIcon,
  ThumbsUpIcon,
} from '../../assets/svg/SVGComponents';
import PostActionsMenu from '../PostActionsMenu';
import PostComment from '../PostComment';
import PostVideo from '../PostVideo';
import api from '../../services/api';
import Input from '../Input';
import PrimaryButton from '../Buttons/PrimaryButton';
import getValidationErrors from '../../utils/getValidationErrors';
import { gradeTexts, hardwareTexts } from '../../utils/enums';
import { useAuth } from '../../hooks/auth';

interface IPostProps {
  id: string;
  user: IUserPost;
  grade: string;
  hardware: string;
  tags: string[];
  title: string;
  description: string;
  photos: IPostMedia[];
  likes: number;
  is_liked_by_user: boolean;
  comments: IShowComment[];
  date_creation: string;
  hideBorderRadius?: boolean;
  showComments?: boolean;
  /** Chamado depois que o projeto sai do ar, para quem lista removê-lo. */
  onDeleted?: (post_id: string) => void;
}

interface ITagPost {
  border: boolean;
  color: string;
  text: string;
}

const PostProjectComponent: React.FC<IPostProps> = ({
  id,
  user: userPost,
  grade,
  hardware,
  tags,
  title,
  description,
  photos,
  likes,
  is_liked_by_user,
  comments,
  date_creation,
  hideBorderRadius = false,
  showComments = false,
  onDeleted,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [backButtonDisable, setBackButtonDisable] = useState(false);
  const [totalImages, setTotalSlides] = useState(0);
  const [enableButtons, setEnableButtons] = useState(false);
  const [nextButtonDisable, setNextButtonDisable] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [countLikes, setCountLikes] = useState(likes);
  const [commentPost, setCommentsPost] = useState<IShowComment[]>(comments);
  const [openComments, setOpenComments] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  /** Só o autor tem ações sobre o post; o menu some para todo mundo mais. */
  const isPostAuthor = userPost.user_id === user.id;

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError('');

    try {
      await api.delete(`/posts/${id}`);
      onDeleted?.(id);
    } catch {
      setDeleteError('Não foi possível excluir. Tente de novo.');
      setIsDeleting(false);
    }
  }, [id, onDeleted]);
  const [formatedDate, setFormatedDate] = useState('');
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isliked, setIsLiked] = useState(is_liked_by_user);
  const [isClosingComments, setIsClosingComments] = useState(false);

  const handleFormatedDate = useCallback((date: string) => {
    const now = new Date();
    const dataPost = new Date(date);

    if (Number.isNaN(dataPost.getTime())) {
      return 'Data inválida';
    }

    // Diferença em segundos (abs para tolerar pequenas diferenças de clock/timezone)
    const diffInSeconds = Math.floor(
      (now.getTime() - dataPost.getTime()) / 1000,
    );

    // Tolerância: datas até 2 min no futuro são tratadas como "Agora"
    if (diffInSeconds < 0) {
      return 'Agora';
    }

    if (diffInSeconds < 60) {
      return diffInSeconds <= 1 ? 'Agora' : `${diffInSeconds} segundos`;
    }

    // Rest of your function remains the same...
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1
        ? `${diffInMinutes} minuto`
        : `${diffInMinutes} minutos`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    // Se a diferença for inferior a 24 horas, retorna em horas
    if (diffInHours < 24) {
      return diffInHours === 1 ? `${diffInHours} hora` : `${diffInHours} horas`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    // Se a diferença for inferior a 7 dias, retorna em dias
    if (diffInDays < 7) {
      return diffInDays === 1 ? `${diffInDays} dia` : `${diffInDays} dias`;
    }

    // Se a data for do mesmo ano, retorna no formato DD/MM
    if (dataPost.getFullYear() === now.getFullYear()) {
      const dia = dataPost.getDate().toString().padStart(2, '0');
      const mes = (dataPost.getMonth() + 1).toString().padStart(2, '0');
      return `${dia}/${mes}`;
    }

    // Se for de outro ano, retorna no formato DD/MM/AAAA
    const dia = dataPost.getDate().toString().padStart(2, '0');
    const mes = (dataPost.getMonth() + 1).toString().padStart(2, '0');
    const ano = dataPost.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }, []);

  useEffect(() => {
    const date = handleFormatedDate(date_creation);
    setFormatedDate(date);
  }, [date_creation, handleFormatedDate]);

  const Tags = useMemo<ITagPost[]>(() => {
    return [
      {
        border: true,
        color: '#4CAF50',
        text: gradeTexts[grade as keyof typeof gradeTexts],
      },
      {
        border: false,
        color: '#4CAF50',
        text: hardwareTexts[hardware as keyof typeof hardwareTexts],
      },
      ...tags.slice(0, 2).map(tag => ({
        border: false,
        color: '#FF9800',
        text: tag,
      })),
    ];
  }, [grade, hardware, tags]);

  const handleImage = useCallback(() => {
    if (photos) {
      setTotalSlides(photos.length);
    }
  }, [photos]);

  const handleLikePost = useCallback(async () => {
    await api.post('/posts/like', { post_id: id }).then(response => {
      setCountLikes(response.data.likes);
    });
    setIsLiked(true);
  }, [id]);

  const onLike = useCallback(() => {
    // Dispara a lógica de like
    handleLikePost();
    // Ativa a animação
    setIsAnimating(true);
    // Remove a classe após o tempo da animação
    setTimeout(() => setIsAnimating(false), 350);
  }, [handleLikePost]);

  useEffect(() => {
    handleImage();
  }, [handleImage]);

  const moveToPrevSlide = useCallback(() => {
    setCurrentIndex(currentIndex === 0 ? totalImages - 1 : currentIndex - 1);
  }, [currentIndex, totalImages]);

  const moveToNextSlide = useCallback(() => {
    setCurrentIndex(currentIndex === totalImages - 1 ? 0 : currentIndex + 1);
  }, [currentIndex, totalImages]);

  useEffect(() => {
    if (currentIndex) {
      if (currentIndex <= 0) {
        setBackButtonDisable(true);
      } else {
        setBackButtonDisable(false);
      }

      if (currentIndex >= totalImages) {
        setNextButtonDisable(true);
      } else {
        setNextButtonDisable(false);
      }
    }
  }, [currentIndex, totalImages]);

  useEffect(() => {
    if (totalImages) {
      if (totalImages > 1) {
        setEnableButtons(true);
      }
      if (totalImages <= 1) {
        setEnableButtons(false);
      }
    }
  }, [totalImages]);

  const handleDotClick = (index: number): void => {
    setCurrentIndex(index);
  };

  useEffect(() => {
    if (showComments) {
      setOpenComments(true);
    }
  }, [showComments]);

  const handleOpenComments = useCallback(() => {
    if (showComments === true) {
      setOpenComments(true);
      return;
    }

    if (openComments) {
      // Toca animação de fechar antes de desmontar
      setIsClosingComments(true);
      setTimeout(() => {
        setOpenComments(false);
        setIsClosingComments(false);
      }, 260);
    } else {
      setOpenComments(true);
    }
  }, [showComments, openComments]);

  const handleCommentUpdated = useCallback((updated: IShowComment) => {
    setCommentsPost(prev =>
      prev.map(item => (item.id === updated.id ? updated : item)),
    );
  }, []);

  const handleCommentDeleted = useCallback((comment_id: string) => {
    setCommentsPost(prev => prev.filter(item => item.id !== comment_id));
  }, []);

  // enviar comentários
  const handleSubmit = useCallback(
    async (comment: string) => {
      formRef.current?.setErrors({});

      try {
        setIsLoading(true);
        const commentSchema = Yup.object().shape({
          comment: Yup.string().required('Escreva seu comentário.'),
        });

        const validatedComment = await commentSchema.validate(comment, {
          abortEarly: false,
        });

        api
          .post('/posts/comment', {
            post_id: id,
            comment: validatedComment?.comment,
          })
          .then(response => {
            // O backend devolve o comentário pronto (IShowComment), com id e
            // user_id — que é o que libera editar e excluir sem recarregar.
            setCommentsPost(prev => [response.data as IShowComment, ...prev]);

            formRef.current?.reset();

            setCommentSuccess(true);
          });
      } catch (err) {
        setIsLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [id],
  );

  return (
    <Container $hideBorderRadius={hideBorderRadius}>
      <HeaderContent>
        <PostUserContent>
          <UserAvatar
            $avatarSrc={
              userPost.user_avatar ||
              'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
            }
          />
          <section>
            <h2>{userPost.username}</h2>
            <p>{userPost.school}</p>
          </section>
        </PostUserContent>
        <HeaderActions>
          <TagsContainer>
            {Tags.map((tag, index) => (
              <Tag key={index} $border={tag.border} $color={tag.color}>
                {tag.text}
              </Tag>
            ))}
          </TagsContainer>

          {isPostAuthor && (
            <PostActionsMenu
              triggerLabel={`Ações do projeto ${title}`}
              actions={[
                {
                  label: 'Editar',
                  icon: PencilIcon,
                  to: `/edit/post/${id}`,
                },
                {
                  label: 'Excluir',
                  icon: TrashIcon,
                  destructive: true,
                  onSelect: () => setIsConfirmingDelete(true),
                },
              ]}
            />
          )}
        </HeaderActions>
      </HeaderContent>

      {isConfirmingDelete && (
        <DeleteConfirmBar role="alertdialog" aria-label="Confirmar exclusão">
          <p>
            <strong>Excluir este projeto?</strong> Saem junto os arquivos
            enviados
            {commentPost.length > 0 &&
              ` e ${commentPost.length} ${
                commentPost.length === 1
                  ? 'comentário de quem'
                  : 'comentários de quem'
              } comentou`}
            . Não dá para desfazer.
            {deleteError && ` ${deleteError}`}
          </p>

          <ConfirmDeleteButton
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo…' : 'Excluir definitivamente'}
          </ConfirmDeleteButton>

          <CancelDeleteButton
            type="button"
            onClick={() => setIsConfirmingDelete(false)}
            disabled={isDeleting}
          >
            Cancelar
          </CancelDeleteButton>
        </DeleteConfirmBar>
      )}

      <ContentContainer>
        <h1>{title}</h1>

        <span>{description}</span>

        {/* Toda a mídia pode ter sido rejeitada pela moderação — sem
            isto sobraria o quadrado cinza do wrapper, vazio. */}
        {photos.length > 0 && (
          <CarouselWrapper>
            <CarouselTrack
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {photos.map((media, index) => (
                <CarouselSlide key={media.url}>
                  {media.moderation_status === 'pending_review' && (
                    <ReviewTag>Em avaliação</ReviewTag>
                  )}
                  {media.media_type === 'video' ? (
                    // O #t=0.1 força o navegador a pintar o primeiro quadro em
                    // vez de mostrar preto até o play.
                    <PostVideo
                      src={`${media.url}#t=0.1`}
                      isActive={index === currentIndex}
                    />
                  ) : (
                    <img src={media.url} alt={`Post ${index + 1}`} />
                  )}
                </CarouselSlide>
              ))}
            </CarouselTrack>

            {enableButtons && (
              <ButtonContainer
                onMouseEnter={() => setShowButtons(true)}
                onMouseLeave={() => setShowButtons(false)}
                $hover={showButtons}
              >
                <button
                  type="button"
                  disabled={backButtonDisable}
                  onClick={() => moveToPrevSlide()}
                >
                  <ArrowBackIcon
                    width="1.22rem"
                    stroke="#fff"
                    strokeWidth={4}
                    fill="none"
                  />
                </button>

                <button
                  type="button"
                  disabled={nextButtonDisable}
                  onClick={() => moveToNextSlide()}
                >
                  <ArrowBackIcon
                    width="1.22rem"
                    stroke="#fff"
                    strokeWidth={4}
                    fill="none"
                    transform="scale(-1, 1)"
                  />
                </button>
              </ButtonContainer>
            )}
          </CarouselWrapper>
        )}
        {enableButtons && (
          <Indicators $width="medium">
            <button
              type="button"
              disabled={backButtonDisable}
              onClick={() => moveToPrevSlide()}
            >
              <ArrowBackIcon />
            </button>
            {currentIndex >= 0 && (
              <DotSlides $dots={photos.length}>
                {photos.map((image, index) => (
                  <Dot
                    key={index}
                    $width="large"
                    onClick={() => handleDotClick(index)}
                  >
                    <DotAccomplished
                      $accomplished={index + 1 <= currentIndex + 1}
                      $width="large"
                    />
                  </Dot>
                ))}
              </DotSlides>
            )}

            <button
              type="button"
              disabled={nextButtonDisable}
              onClick={() => moveToNextSlide()}
            >
              <ArrowBackIcon transform="scale(-1, 1)" />
            </button>
          </Indicators>
        )}
      </ContentContainer>
      <Footer>
        <div>
          <LikeButton type="button" onClick={() => onLike()}>
            <ThumbsUpIcon
              fillArmColor={isliked ? '#F3B4E7' : '#fff'}
              fillHandColor={isliked ? '#1e88e5' : '#fff'}
              width="2.2rem"
              stroke="#323232"
              strokeWidth={3}
              className={isAnimating ? 'animate-thumbs' : ''}
            />
            <span>{countLikes}</span>
          </LikeButton>
          <CommentsButton type="button" onClick={() => handleOpenComments()}>
            <CommentsIcon width="2.2rem" stroke="#323232" strokeWidth={3} />
            <span>{commentPost.length}</span>
          </CommentsButton>
        </div>

        <span>{formatedDate}</span>
      </Footer>

      {openComments && (
        <CommentsContainer $closing={isClosingComments}>
          <CommentInput>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <UserAvatarComments
                $avatarSrc={
                  user.avatar_url ||
                  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                }
              />
              <InputWrapper>
                <Input
                  name="comment"
                  type="text"
                  placeholder="Digite seu comentário"
                />
              </InputWrapper>

              <PrimaryButton loading={isLoading} success={commentSuccess}>
                <SendMessageIcon
                  width="1.4rem"
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              </PrimaryButton>
            </Form>
          </CommentInput>
          <CommentsWrapper>
            <h1>Comentários</h1>
            {commentPost.map(postComment => (
              <PostComment
                key={postComment.id}
                comment={postComment}
                isOwn={postComment.user_id === user.id}
                onUpdated={handleCommentUpdated}
                onDeleted={handleCommentDeleted}
              />
            ))}
          </CommentsWrapper>
        </CommentsContainer>
      )}
    </Container>
  );
};

export default PostProjectComponent;
